import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { getAccuracyByEstimate } from '../../utils/analyticsApi';

const AccuracyByEstimate = ({ scope, organizationId, teamId }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, organizationId, teamId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { scope };
      if (organizationId) params.organizationId = organizationId;
      if (teamId) params.teamId = teamId;
      const response = await getAccuracyByEstimate(params);
      setData(response.data.byEstimate || []);
    } catch (err) {
      console.error('Error fetching accuracy by estimate:', err);
      setError('Failed to load accuracy by estimate');
    } finally {
      setLoading(false);
    }
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 70) return '#10B981';
    if (accuracy >= 50) return '#F59E0B';
    return '#EF4444';
  };

  const getDirectionIcon = (direction) => {
    switch (direction) {
      case 'underestimate':
        return <TrendingUpIcon sx={{ fontSize: 16, color: '#EF4444' }} />;
      case 'overestimate':
        return <TrendingDownIcon sx={{ fontSize: 16, color: '#3B82F6' }} />;
      default:
        return <RemoveIcon sx={{ fontSize: 16, color: '#10B981' }} />;
    }
  };

  const getDirectionLabel = (direction) => {
    switch (direction) {
      case 'underestimate':
        return 'Under';
      case 'overestimate':
        return 'Over';
      default:
        return 'Accurate';
    }
  };

  const getDirectionColor = (direction) => {
    switch (direction) {
      case 'underestimate':
        return { bg: '#FEE2E2', text: '#991B1B' };
      case 'overestimate':
        return { bg: '#DBEAFE', text: '#1E40AF' };
      default:
        return { bg: '#D1FAE5', text: '#065F46' };
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="subtitle2">Estimate: {item.estimate}</Typography>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ color: getAccuracyColor(item.accuracyRate) }}>
              Accuracy: {item.accuracyRate}%
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {item.itemCount} items
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Avg variance: {item.avgVariance > 0 ? '+' : ''}{item.avgVariance}
            </Typography>
          </Box>
        </Paper>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Accuracy by Estimate Size
        </Typography>
        <Skeleton variant="rectangular" height={250} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <AssessmentIcon sx={{ fontSize: 48, color: '#9CA3AF', mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
          No accuracy data by estimate available
        </Typography>
        <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
          Enter actual points for estimated items to see which estimates are most accurate
        </Typography>
      </Box>
    );
  }

  // Calculate average accuracy for reference line
  const avgAccuracy = Math.round(
    data.reduce((sum, d) => sum + d.accuracyRate * d.itemCount, 0) /
    data.reduce((sum, d) => sum + d.itemCount, 0)
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Accuracy by Estimate Size
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Identify which point values your team estimates accurately vs. where you tend to miss
      </Typography>

      {/* Bar Chart */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis
              dataKey="estimate"
              tick={{ fill: '#64748B', fontSize: 12 }}
              axisLine={{ stroke: '#CBD5E1' }}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fill: '#64748B', fontSize: 12 }}
              axisLine={{ stroke: '#CBD5E1' }}
              tickFormatter={(value) => `${value}%`}
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={avgAccuracy}
              stroke="#9CA3AF"
              strokeDasharray="5 5"
              label={{
                value: `Avg ${avgAccuracy}%`,
                fill: '#9CA3AF',
                fontSize: 11,
                position: 'right'
              }}
            />
            <Bar dataKey="accuracyRate" name="Accuracy" radius={[4, 4, 0, 0]}>
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getAccuracyColor(entry.accuracyRate)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* Detailed Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#141414' }}>
              <TableCell sx={{ fontWeight: 600 }}>Estimate</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Items</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Accuracy</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Avg Variance</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Tendency</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => {
              const dirColors = getDirectionColor(row.direction);
              return (
                <TableRow key={row.estimate} hover>
                  <TableCell>
                    <Chip
                      label={row.estimate}
                      size="small"
                      sx={{ fontWeight: 600, minWidth: 40 }}
                    />
                  </TableCell>
                  <TableCell align="center">{row.itemCount}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Box sx={{ width: 60 }}>
                        <LinearProgress
                          variant="determinate"
                          value={row.accuracyRate}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: '#E5E7EB',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: getAccuracyColor(row.accuracyRate),
                              borderRadius: 4
                            }
                          }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: getAccuracyColor(row.accuracyRate),
                          minWidth: 40
                        }}
                      >
                        {row.accuracyRate}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="body2"
                      sx={{
                        color: row.avgVariance > 0 ? '#EF4444' : row.avgVariance < 0 ? '#3B82F6' : '#10B981'
                      }}
                    >
                      {row.avgVariance > 0 ? '+' : ''}{row.avgVariance}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={getDirectionIcon(row.direction)}
                      label={getDirectionLabel(row.direction)}
                      size="small"
                      sx={{
                        bgcolor: dirColors.bg,
                        color: dirColors.text,
                        '& .MuiChip-icon': { color: dirColors.text }
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Insights */}
      <Box sx={{ mt: 2 }}>
        {data.filter(d => d.direction === 'underestimate').length > 0 && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            <Typography variant="body2">
              <strong>Underestimating:</strong>{' '}
              {data.filter(d => d.direction === 'underestimate').map(d => d.estimate).join(', ')} point items tend to take more effort than estimated
            </Typography>
          </Alert>
        )}
        {data.filter(d => d.direction === 'overestimate').length > 0 && (
          <Alert severity="info" sx={{ mb: 1 }}>
            <Typography variant="body2">
              <strong>Overestimating:</strong>{' '}
              {data.filter(d => d.direction === 'overestimate').map(d => d.estimate).join(', ')} point items tend to take less effort than estimated
            </Typography>
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default AccuracyByEstimate;
